import React, { useState, useEffect, useCallback } from "react";
import TablePaginationFooter from "../../ui/molecules/TablePaginationFooter";
import type { AuditLogsScopeType } from "../molecules/AuditLogsScopePill";
import AuditLogsResultPill, {
  type AuditLogsResultType,
} from "../molecules/AuditLogsResultPill";
import AuditLogsViewButton from "../molecules/AuditLogsViewButton";
import AuditLogDetailDrawer, {
  type AuditLogDetailEntry,
} from "./AuditLogDetailDrawer";
import { listAuditLogs, type AuditLogItem } from "../../../api/accessManagement/auditLogsService";
import { formatDateTimeWithTime } from "../../../utils/formatUtils";

interface AuditLogEntry extends AuditLogDetailEntry {
  result: AuditLogsResultType;
  isHighlighted?: boolean;
  action?: string;
  targetType?: string;
  actorName?: string;
}

function mapApiLogToEntry(api: AuditLogItem): AuditLogEntry {
  const id = (api._id || api.id || "").toString();
  const timestampRaw = api.timestamp || api.createdAt || "";
  const timestamp = formatDateTimeWithTime(timestampRaw);
  const actorName = api.actorName || "";
  const actor = actorName;
  const actedOn =
    api.actedOnName ||
    api.actedon ||
    api.actedOn ||
    api.target ||
    api.targetName ||
    "";
  const permission = api.permission || api.permissionCode || "";
  const before = api.before ?? api.previousValue ?? "";
  const after = api.after ?? api.newValue ?? "";
  const scopeRaw = api.scope || api.scopeType || "";
  const scope = (scopeRaw || "PLATFORM").toString().toUpperCase() as AuditLogsScopeType;
  const scopeId = api.scopeId || api.scope_id || "";
  const resource = api.resource || api.resourceType || "";
  const resultRaw = (api.result || api.outcome || "Success").toString().toLowerCase();
  const result: AuditLogsResultType =
    resultRaw === "failed" || resultRaw === "failure" ? "Failed" : "Success";
  const details = api.details ?? null;

  return {
    id,
    timestamp,
    action: api.action,
    actor,
    actorName,
    actedOn,
    permission,
    before,
    after,
    scope,
    scopeId,
    resource,
    result,
    targetType: api.targetType,
    details,
  };
}

interface AuditLogsTableProps {
  onTotalChange?: (total: number) => void;
  action?: string;
  permissionCode?: string;
  actor?: string;
  from?: string;
  to?: string;
}

const AuditLogsTable: React.FC<AuditLogsTableProps> = ({
  onTotalChange,
  action,
  permissionCode,
  actor,
  from,
  to,
}) => {
  const [selectedLog, setSelectedLog] = useState<AuditLogDetailEntry | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleView = (log: AuditLogEntry) => {
    setSelectedLog(log);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedLog(null);
  };

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, meta } = await listAuditLogs({
        page,
        limit: pageSize,
        action: action || undefined,
        targetId: permissionCode || undefined,
        actor: actor?.trim() || undefined,
        from,
        to,
      });
      const mapped = (data || []).map(mapApiLogToEntry);
      setLogs(mapped);
      setTotalCount(meta.total);
      setTotalPages(meta.pages || 1);
      if (onTotalChange) {
        onTotalChange(meta.total);
      }
    } catch (err) {
      console.error("Failed to fetch audit logs:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch audit logs");
      setLogs([]);
      setTotalCount(0);
      setTotalPages(1);
      if (onTotalChange) {
        onTotalChange(0);
      }
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, action, permissionCode, actor, from, to, onTotalChange]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(1);
  }, [action, permissionCode, actor, from, to]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <>
      <div className="access-management-table-wrapper">
        {loading ? (
          <div className="text-center py-5 text-muted">Loading audit logs...</div>
        ) : error ? (
          <div className="text-center py-5 text-danger">{error}</div>
        ) : (
          <div className="table-responsive">
            <table className="access-management-table table align-middle table-hover mb-0 audit-logs-table">
              <thead className="table-light">
                <tr>
                  <th scope="col">Timestamp</th>
                  <th scope="col">Action</th>
                  <th scope="col">Actor</th>
                  <th scope="col">Acted On</th>
                  <th scope="col">Target Type</th>
                  <th scope="col">Result</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-muted">
                      No audit logs found
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id}>
                      <td>
                        <span className="audit-logs-table-timestamp">
                          {log.timestamp}
                        </span>
                      </td>
                      <td>
                        <span className="audit-logs-table-action">
                          {log.action || "-"}
                        </span>
                      </td>
                      <td>
                        <span className="audit-logs-table-actor">{log.actor || '-'}</span>
                      </td>
                      <td>
                        <span className="audit-logs-table-acted-on">
                          {log.actedOn}
                        </span>
                      </td>
                      <td>
                        <span className="audit-logs-table-target-type">
                          {log.targetType || "-"}
                        </span>
                      </td>
                      <td className="audit-logs-result-cell">
                        <AuditLogsResultPill value={log.result} />
                        <AuditLogsViewButton onClick={() => handleView(log)} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <TablePaginationFooter
        page={page}
        totalPages={Math.max(1, totalPages)}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />
      <AuditLogDetailDrawer
        show={drawerOpen}
        onHide={handleCloseDrawer}
        log={selectedLog}
      />
    </>
  );
};

export default AuditLogsTable;
